import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import ArticleCard from '@/components/base/ArticleCard';
import useSeo from '@/hooks/useSeo';
import { articles, siteInfo } from '@/mocks/incidents';

interface BodyBlock {
  type: string;
  text?: string;
  items?: string[];
}

export default function CaseDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((item) => item.id === id);
  const [copied, setCopied] = useState(false);

  const headings = useMemo(() => {
    if (!article) return [];
    return (article.body as BodyBlock[]).filter((block) => block.type === 'h2').map((block) => block.text as string);
  }, [article]);

  const related = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((item) => item.id !== article.id)
      .sort((a, b) => {
        const scoreA = (a.category === article.category ? 2 : 0) + (a.region === article.region ? 1 : 0);
        const scoreB = (b.category === article.category ? 2 : 0) + (b.region === article.region ? 1 : 0);
        return scoreB - scoreA;
      })
      .slice(0, 3);
  }, [article]);

  useSeo({
    title: article ? `${article.title}｜缅北黑料` : '条目未找到｜缅北黑料',
    description: article
      ? `${article.summary.slice(0, 90)}——来自缅北黑料的追踪整理，持续关注跨境犯罪打击动态。`
      : '你访问的条目不存在或已被调整，可以返回案件通报栏目查看全部追踪条目。',
    keywords: article ? `${article.tags.join(',')},缅北黑料,${article.category}` : '缅北黑料,案件通报',
    path: article ? `/cases/${article.id}` : '/cases',
    ogType: 'article',
    jsonLd: article
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://thcihd.cn/' },
                { '@type': 'ListItem', position: 2, name: '案件通报', item: 'https://thcihd.cn/cases' },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                  item: `https://thcihd.cn/cases/${article.id}`,
                },
              ],
            },
            {
              '@type': 'NewsArticle',
              headline: article.title,
              description: article.summary,
              datePublished: article.date,
              dateModified: article.date,
              inLanguage: 'zh-CN',
              articleSection: article.category,
              keywords: article.tags.join(','),
              author: { '@type': 'Organization', name: siteInfo.name },
              publisher: { '@type': 'Organization', name: siteInfo.name },
              mainEntityOfPage: `https://thcihd.cn/cases/${article.id}`,
            },
          ],
        }
      : undefined,
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const scrollToHeading = (text: string) => {
    const target = document.getElementById(`heading-${text}`);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!article) {
    return (
      <PageShell>
        <section className="mx-auto flex max-w-content flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
            <i className="ri-error-warning-line"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">条目未找到</h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            该条目可能已被调整或链接有误。你可以返回案件通报栏目，浏览当前全部追踪内容。
          </p>
          <Link
            to="/cases"
            className="mt-6 rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            返回案件通报
          </Link>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-6 md:px-6 md:py-8">
          <Breadcrumbs
            items={[{ label: '首页', to: '/' }, { label: '案件通报', to: '/cases' }, { label: article.category }]}
          />
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <article className="lg:col-span-8">
              <header>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center rounded-sm bg-primary-600 px-2.5 py-1 text-[11px] font-medium text-background-50">
                    {article.category}
                  </span>
                  <Link
                    to={`/cases?region=${encodeURIComponent(article.region)}`}
                    className="flex items-center gap-1 text-[11px] text-foreground-500 transition-colors hover:text-primary-600"
                  >
                    <i className="ri-map-pin-line"></i>
                    {article.region}
                  </Link>
                </div>
                <h1 className="mt-4 font-heading text-2xl font-bold leading-snug tracking-tight text-foreground-950 md:text-3xl">
                  {article.title}
                </h1>
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-4 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-file-paper-2-line"></i>
                    {article.source}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line"></i>
                    约 {article.readMinutes} 分钟阅读
                  </span>
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="ml-auto flex items-center gap-1.5 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className={copied ? 'ri-check-line' : 'ri-link'}></i>
                    {copied ? '已复制' : '复制链接'}
                  </button>
                </div>
              </header>

              <figure className="mt-6 overflow-hidden rounded-lg bg-background-200">
                <div className="h-[240px] w-full md:h-[380px]">
                  <img
                    src={article.cover}
                    alt={`${article.title} 相关配图`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <figcaption className="bg-background-100 px-4 py-2.5 text-[11px] text-foreground-500">
                  配图为示意图片，与本条目无直接对应关系，仅用于版式呈现。
                </figcaption>
              </figure>

              <div className="prose-article mt-8">
                {(article.body as BodyBlock[]).map((block, index) => {
                  if (block.type === 'h2') {
                    return (
                      <h2
                        key={index}
                        id={`heading-${block.text}`}
                        className="mb-3 mt-8 scroll-mt-28 font-heading text-lg font-bold text-foreground-950 md:text-xl"
                      >
                        {block.text}
                      </h2>
                    );
                  }
                  if (block.type === 'quote') {
                    return (
                      <blockquote
                        key={index}
                        className="my-6 rounded-r-lg border-l-4 border-primary-500 bg-primary-50/60 px-5 py-4 font-heading text-sm leading-relaxed text-primary-900"
                      >
                        {block.text}
                      </blockquote>
                    );
                  }
                  if (block.type === 'list') {
                    return (
                      <ul key={index} className="my-5 space-y-2.5">
                        {(block.items ?? []).map((item) => (
                          <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700">
                            <i className="ri-checkbox-blank-circle-fill mt-1.5 text-[7px] text-accent-600"></i>
                            {item}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return <p key={index}>{block.text}</p>;
                })}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="mr-1 text-xs text-foreground-500">标签</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/cases?category=${encodeURIComponent(article.category)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800 transition-colors hover:bg-secondary-200"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 p-5">
                <p className="flex items-start gap-2 text-xs leading-relaxed text-accent-900">
                  <i className="ri-information-line mt-0.5 text-base"></i>
                  <span>
                    本条目基于公开信息整理，仅用于信息参考与反诈宣传，不构成任何法律意见，也不对个案作出定性判断。
                    如需处理具体事务，请通过官方渠道反映。
                  </span>
                </p>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                {headings.length > 0 && (
                  <div className="rounded-xl border border-background-200 bg-background-50 p-5">
                    <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                      <i className="ri-list-check-2 text-primary-600"></i>
                      本页目录
                    </h2>
                    <ul className="mt-3 space-y-1">
                      {headings.map((heading) => (
                        <li key={heading}>
                          <button
                            type="button"
                            onClick={() => scrollToHeading(heading)}
                            className="w-full rounded-md px-3 py-2 text-left text-xs leading-relaxed text-foreground-600 transition-colors hover:bg-background-100 hover:text-primary-700"
                          >
                            {heading}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="mt-5 rounded-xl border border-background-200 bg-background-100 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                    <i className="ri-file-info-line text-primary-600"></i>
                    条目信息
                  </h2>
                  <dl className="mt-3 space-y-2 text-xs">
                    <div className="flex justify-between gap-4">
                      <dt className="text-foreground-500">整理来源</dt>
                      <dd className="text-right text-foreground-800">{article.source}</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-foreground-500">发布时间</dt>
                      <dd className="text-foreground-800">{article.date}</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-foreground-500">涉及地区</dt>
                      <dd className="text-foreground-800">{article.region}</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-foreground-500">所属分类</dt>
                      <dd className="text-foreground-800">{article.category}</dd>
                    </div>
                  </dl>
                </div>

                <div className="mt-5">
                  <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-950">
                    <i className="ri-links-line text-primary-600"></i>
                    相关条目
                  </h2>
                  <div className="mt-3 space-y-3">
                    {related.map((item) => (
                      <Link
                        key={item.id}
                        to={`/cases/${item.id}`}
                        className="group block rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200"
                      >
                        <span className="text-[11px] text-foreground-500">{item.category}</span>
                        <span className="mt-1 line-clamp-2 block text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-700">
                          {item.title}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>

          <div className="mt-14 border-t border-background-200 pt-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950">更多追踪条目</h2>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}